import React, { useCallback, useEffect, useMemo, useState } from "react";
import { onBackdropMouseDown, onModalPanelMouseDown } from "../lib/modal";
import { api } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { useBranch } from "@/context/BranchContext";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Building2, Plus, X, Package as PackageIcon, ChevronDown, Printer } from "lucide-react";
import { fmt } from "@/lib/format";
import { printTransfer } from "@/lib/printTransfer";

const emptyForm = (defaultFrom = "", defaultTo = "") => ({
  from_branch_id: defaultFrom,
  to_branch_id: defaultTo,
  notes: "",
  items: [{ product_id: "", qty: 1 }],
});

export default function InventoryTransfers() {
  const { t, lang } = useLanguage();
  const { branches, activeBranchId } = useBranch();
  const [transfers, setTransfers] = useState([]);
  const [products, setProducts] = useState([]);     // products with branch-scoped stock for the from-branch
  const [invoiceTemplate, setInvoiceTemplate] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const branchLabel = useCallback((b) => (lang === "ar" ? (b?.name_ar || b?.name_en) : (b?.name_en || b?.name_ar)) || b?.branch_id, [lang]);

  const branchMap = useMemo(() => {
    const m = {};
    branches.forEach((b) => { m[b.branch_id] = branchLabel(b); });
    return m;
  }, [branches, branchLabel]);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/inventory-transfers?limit=200");
      setTransfers(r.data || []);
    } catch (e) {
      console.error("[Transfers] load failed", e);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Load print template once for invoice headers/branding.
  useEffect(() => {
    api.get("/invoice-template")
      .then((r) => setInvoiceTemplate(r.data || null))
      .catch(() => setInvoiceTemplate(null));
  }, []);

  // Whenever the from-branch in the modal changes, re-fetch products
  // scoped to that branch so the "available" hint is accurate.
  useEffect(() => {
    if (!open || !form.from_branch_id) {
      setProducts([]);
      return;
    }
    let cancelled = false;
    api.get(`/products?branch_id=${encodeURIComponent(form.from_branch_id)}`)
      .then((r) => { if (!cancelled) setProducts(r.data || []); })
      .catch(() => { if (!cancelled) setProducts([]); });
    return () => { cancelled = true; };
  }, [open, form.from_branch_id]);

  const openNew = () => {
    const from = activeBranchId || branches[0]?.branch_id || "";
    const to = branches.find((b) => b.branch_id !== from)?.branch_id || "";
    setForm(emptyForm(from, to));
    setOpen(true);
  };

  const addLine = () => setForm((f) => ({ ...f, items: [...f.items, { product_id: "", qty: 1 }] }));
  const removeLine = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const updateLine = (i, field, val) => setForm((f) => ({
    ...f, items: f.items.map((x, idx) => idx === i ? { ...x, [field]: val } : x),
  }));

  const submit = async () => {
    if (!form.from_branch_id || !form.to_branch_id) {
      toast.error(lang === "ar" ? "اختر فرعَي المصدر والوجهة" : "Select both branches");
      return;
    }
    if (form.from_branch_id === form.to_branch_id) {
      toast.error(lang === "ar" ? "يجب اختيار فرعين مختلفين" : "Source and destination must differ");
      return;
    }
    const items = form.items
      .filter((i) => i.product_id && parseInt(i.qty) > 0)
      .map((i) => ({ product_id: i.product_id, qty: parseInt(i.qty) }));
    if (items.length === 0) {
      toast.error(lang === "ar" ? "أضف منتجاً واحداً على الأقل" : "Add at least one item");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/inventory-transfers", {
        from_branch_id: form.from_branch_id,
        to_branch_id: form.to_branch_id,
        notes: form.notes || "",
        items,
      });
      toast.success(lang === "ar" ? `تم إنشاء التحويل ${r.data.transfer_number}` : `Transfer ${r.data.transfer_number} created`);
      setOpen(false);
      setForm(emptyForm());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || (lang === "ar" ? "تعذر التحويل" : "Transfer failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const ArrowIcon = lang === "ar" ? ArrowLeft : ArrowRight;

  return (
    <div className="space-y-6" data-testid="inventory-transfers-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl gold-text flex items-center gap-2">
            <ArrowUpDown />
            {lang === "ar" ? "تحويلات بين الفروع" : "Branch Transfers"}
          </h1>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            {lang === "ar" ? "نقل مخزون المنتجات من فرع إلى آخر مع تتبع كامل" : "Move stock between branches with full audit trail"}
          </p>
        </div>
        <button onClick={openNew} data-testid="new-transfer-btn" className="btn-gold px-4 py-2 rounded-md flex items-center gap-2 text-sm">
          <Plus size={16}/>{lang === "ar" ? "تحويل جديد" : "New Transfer"}
        </button>
      </div>

      {/* Transfer list */}
      <div className="card-surface overflow-auto">
        <table className="table-dark">
          <thead>
            <tr>
              <th>{lang === "ar" ? "الرقم" : "Number"}</th>
              <th>{lang === "ar" ? "التاريخ" : "Date"}</th>
              <th>{lang === "ar" ? "من" : "From"}</th>
              <th>{lang === "ar" ? "إلى" : "To"}</th>
              <th>{lang === "ar" ? "العناصر" : "Items"}</th>
              <th>{lang === "ar" ? "إجمالي القطع" : "Total Qty"}</th>
              <th>{lang === "ar" ? "بواسطة" : "By"}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {transfers.map((tr) => (
              <React.Fragment key={tr.transfer_id}>
                <tr className="cursor-pointer hover:bg-[var(--bg)]" onClick={() => setExpanded(expanded === tr.transfer_id ? null : tr.transfer_id)} data-testid={`transfer-row-${tr.transfer_id}`}>
                  <td className="text-[var(--gold)] font-mono">{tr.transfer_number}</td>
                  <td className="text-xs text-[var(--text-muted)]">{tr.created_at?.slice(0, 16).replace("T", " ")}</td>
                  <td>
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-rose-50 text-rose-700">
                      <Building2 size={10}/>{tr.from_branch_name || branchMap[tr.from_branch_id] || "—"}
                    </span>
                  </td>
                  <td className="flex items-center gap-2">
                    <ArrowIcon size={14} className="text-[var(--gold)]"/>
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                      <Building2 size={10}/>{tr.to_branch_name || branchMap[tr.to_branch_id] || "—"}
                    </span>
                  </td>
                  <td className="text-center num-display">{tr.items_count}</td>
                  <td className="text-center gold-text font-semibold num-display">{tr.total_qty}</td>
                  <td className="text-xs text-[var(--text-muted)]">{tr.created_by_name || "—"}</td>
                  <td>
                    <div className="flex items-center gap-1.5" onMouseDown={onModalPanelMouseDown}>
                      <button
                        onClick={() => printTransfer(tr, invoiceTemplate, lang)}
                        data-testid={`print-transfer-${tr.transfer_id}`}
                        title={lang === "ar" ? "طباعة" : "Print"}
                        className="p-1.5 rounded border border-[var(--border)] hover:border-[var(--gold)] hover:text-[var(--gold)]">
                        <Printer size={14}/>
                      </button>
                      <button
                        onClick={() => setExpanded(expanded === tr.transfer_id ? null : tr.transfer_id)}
                        title={lang === "ar" ? "تفاصيل" : "Details"}
                        className="p-1.5 rounded border border-[var(--border)] hover:border-[var(--gold)] hover:text-[var(--gold)]">
                        <ChevronDown size={14} className={`transition-transform ${expanded === tr.transfer_id ? "rotate-180" : ""}`}/>
                      </button>
                    </div>
                  </td>
                </tr>
                {expanded === tr.transfer_id && (
                  <tr>
                    <td colSpan={8} className="bg-[var(--bg)] p-0">
                      <div className="p-4 space-y-2">
                        {(tr.items || []).map((it, i) => (
                          <div key={`${tr.transfer_id}-${i}`} className="flex items-center justify-between bg-[var(--surface)] p-2 rounded border border-[var(--border-subtle)]">
                            <div className="flex items-center gap-2 text-sm">
                              <PackageIcon size={14} className="text-[var(--gold)]"/>
                              <span className="text-[var(--text)]">{lang === "ar" ? it.name_ar : it.name_en}</span>
                              {it.sku && <span className="text-xs text-[var(--text-muted)]">({it.sku})</span>}
                            </div>
                            <span className="text-sm font-mono gold-text">×{it.qty}</span>
                          </div>
                        ))}
                        {tr.notes && (
                          <div className="text-xs text-[var(--text-muted)] mt-2 italic">
                            {lang === "ar" ? "ملاحظات: " : "Notes: "}{tr.notes}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {transfers.length === 0 && (
              <tr><td colSpan={8} className="text-center text-[var(--text-muted)] py-12">{lang === "ar" ? "لا توجد تحويلات بعد" : "No transfers yet"}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* New transfer modal */}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onMouseDown={(e) => onBackdropMouseDown(e, () => setOpen(false))}>
          <div className="card-surface p-6 w-full max-w-3xl space-y-4 max-h-[92vh] overflow-auto" onMouseDown={onModalPanelMouseDown} data-testid="new-transfer-modal">
            <h2 className="font-heading text-2xl gold-text flex items-center gap-2">
              <ArrowUpDown size={22}/>
              {lang === "ar" ? "تحويل مخزون جديد" : "New Stock Transfer"}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[var(--text-muted)] flex items-center gap-1"><Building2 size={12}/>{lang === "ar" ? "من الفرع" : "From Branch"}</label>
                <select className="input-dark" value={form.from_branch_id} onChange={(e) => setForm({ ...form, from_branch_id: e.target.value, items: [{ product_id: "", qty: 1 }] })} data-testid="transfer-from-branch">
                  <option value="">{lang === "ar" ? "اختر..." : "Select..."}</option>
                  {branches.map((b) => (
                    <option key={b.branch_id} value={b.branch_id}>{branchLabel(b)}{b.is_default ? " ★" : ""}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-[var(--text-muted)] flex items-center gap-1"><Building2 size={12}/>{lang === "ar" ? "إلى الفرع" : "To Branch"}</label>
                <select className="input-dark" value={form.to_branch_id} onChange={(e) => setForm({ ...form, to_branch_id: e.target.value })} data-testid="transfer-to-branch">
                  <option value="">{lang === "ar" ? "اختر..." : "Select..."}</option>
                  {branches.filter((b) => b.branch_id !== form.from_branch_id).map((b) => (
                    <option key={b.branch_id} value={b.branch_id}>{branchLabel(b)}{b.is_default ? " ★" : ""}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <div className="grid grid-cols-12 gap-2 text-xs text-[var(--text-muted)] uppercase tracking-wide px-2">
                <div className="col-span-7">{lang === "ar" ? "المنتج" : "Product"}</div>
                <div className="col-span-2">{lang === "ar" ? "الكمية" : "Qty"}</div>
                <div className="col-span-2">{lang === "ar" ? "المتاح" : "Available"}</div>
                <div className="col-span-1"></div>
              </div>
              {form.items.map((it, i) => {
                const prod = products.find((p) => p.product_id === it.product_id);
                const available = prod ? prod.stock : 0;
                const qty = parseInt(it.qty) || 0;
                const over = qty > available;
                return (
                  <div key={`row-${i}`} className="grid grid-cols-12 gap-2 items-start">
                    <select className="input-dark col-span-7" value={it.product_id} onChange={(e) => updateLine(i, "product_id", e.target.value)} data-testid={`transfer-product-${i}`}>
                      <option value="">{lang === "ar" ? "اختر منتج..." : "Select product..."}</option>
                      {products.filter((p) => p.stock > 0).map((p) => (
                        <option key={p.product_id} value={p.product_id}>{lang === "ar" ? p.name_ar : p.name_en} {p.sku ? `(${p.sku})` : ""} — {p.stock} {lang === "ar" ? "متوفر" : "available"}</option>
                      ))}
                    </select>
                    <input
                      type="number"
                      min="1"
                      max={available}
                      className={`input-dark col-span-2 num-display ${over ? "border-rose-500" : ""}`}
                      value={it.qty}
                      onChange={(e) => updateLine(i, "qty", e.target.value)}
                      data-testid={`transfer-qty-${i}`}
                    />
                    <div className={`col-span-2 text-sm font-mono pt-2 ${over ? "text-rose-500" : "text-[var(--text-muted)]"}`}>
                      {prod ? available : "—"}
                    </div>
                    <button onClick={() => removeLine(i)} className="col-span-1 p-2 rounded border border-[var(--border)] hover:border-rose-500 flex items-center justify-center" data-testid={`transfer-remove-${i}`}>
                      <X size={14}/>
                    </button>
                  </div>
                );
              })}
              <button onClick={addLine} className="btn-rose-outline px-4 py-2 rounded-md text-sm flex items-center gap-2" data-testid="transfer-add-line">
                <Plus size={14}/>{lang === "ar" ? "إضافة منتج" : "Add product"}
              </button>
            </div>

            <div>
              <label className="text-xs text-[var(--text-muted)]">{lang === "ar" ? "ملاحظات (اختياري)" : "Notes (optional)"}</label>
              <textarea className="input-dark" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="transfer-notes"/>
            </div>

            <div className="flex gap-3 justify-end pt-3 border-t border-[var(--border-subtle)]">
              <button className="px-4 py-2 rounded-md border border-[var(--border)]" onClick={() => setOpen(false)}>{t("cancel")}</button>
              <button className="btn-gold px-6 py-2 rounded-md" disabled={submitting} onClick={submit} data-testid="transfer-save-btn">
                {submitting ? (lang === "ar" ? "جارٍ التحويل..." : "Transferring...") : (lang === "ar" ? "تنفيذ التحويل" : "Execute Transfer")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Lightweight icon alias (we already import ArrowUpDown elsewhere via lucide)
function ArrowUpDown(props) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={props.size || 16} height={props.size || 16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="m21 16-4 4-4-4"/><path d="M17 20V4"/><path d="m3 8 4-4 4 4"/><path d="M7 4v16"/>
    </svg>
  );
}
